/**
 * Influencer Data Model
 * 
 * Core data structures and Firestore operations for influencer profiles.
 * Designed to scale to thousands of influencers with efficient queries.
 * 
 * Firestore Collections:
 * - `influencers`: Main profile documents (indexed by userId and username)
 * - `influencerFeed`: Feed posts (top-level collection for query flexibility)
 * 
 * Indexes Required:
 * - influencers: username (ascending) - for username lookups
 * - influencerFeed: influencerId (ascending), createdAt (descending) - for feed queries
 */

import { 
  collection, 
  doc, 
  getDoc, 
  getDocFromServer,
  getDocs, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  startAfter,
  QueryDocumentSnapshot,
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { claimUsernameForUser } from '../../services/usernameService';
import type { SocialLink } from '../social/types';

// ============================================================================
// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Deep sanitize object to remove all undefined values (Firestore rejects them)
 */
function sanitizeForFirestore(obj: any): any {
  if (obj === null || obj === undefined) {
    return null;
  }
  
  if (Array.isArray(obj)) {
    return obj
      .map(item => sanitizeForFirestore(item))
      .filter(item => item !== undefined);
  }
  
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        const sanitized = sanitizeForFirestore(value);
        if (sanitized !== undefined) {
          cleaned[key] = sanitized;
        }
      }
    }
    return cleaned;
  }
  
  return obj;
}

// TYPE DEFINITIONS
// ============================================================================

/**
 * Platform information for an influencer
 */
export interface Platform {
  platformName: 'tiktok' | 'instagram' | 'youtube' | 'twitter' | 'x' | 'other';
  handle: string;
  followers: number;
  avgViews?: number;
  engagementRate?: number;
  url?: string;
}

/**
 * External link (website, Linktree, etc.)
 */
export interface Link {
  label: string;
  url: string;
}

/**
 * Featured video (media kit highlight)
 */
export interface FeaturedVideo {
  id: string; // Random ID for React keys
  title: string; // Short label, e.g., "Sephora UGC – 120K views"
  platform: 'tiktok' | 'instagram' | 'youtube' | 'other';
  url: string; // Full video URL
}

/**
 * Featured post with thumbnail (for visual grid)
 */
export interface FeaturedPost {
  id: string;
  platform: 'tiktok' | 'instagram' | 'youtube' | 'other';
  title?: string;
  thumbnailUrl: string; // Stored image or pulled from platform
  videoUrl: string; // Actual link to post
}

/**
 * Brand partner (logo grid)
 */
export interface BrandPartner {
  id: string;
  name: string;
  logoUrl: string;
}

/**
 * Past brand collaboration
 * Note: This is self-contained. Future brand/campaign features will use
 * separate collections (brands, campaigns, applications) and reference
 * influencer IDs, not embed data here.
 */
/** Crop area in pixels (e.g. from react-easy-crop). Stored for optional re-use. */
export interface ThumbnailCrop {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface Collaboration {
  id: string;
  brandName: string;
  description?: string;
  brandLogoUrl?: string;
  role?: string; // e.g., "TikTok UGC Creator"
  dateRange?: string; // e.g., "2024" or "Aug 2024 – Nov 2024"
  deliverables?: string; // "3 TikToks + 2 IG Reels"
  summary?: string; // Short case-study style description
  results?: string; // "85k views, 12% engagement"
  videoUrl?: string;
  completedAt: number; // Unix timestamp
  metrics?: {
    views?: number;
    likes?: number;
    saves?: number;
    ctr?: number;
    engagement?: number;
  };
  /** Legacy or single-upload thumbnail URL. Prefer thumbnailCroppedUrl when present. */
  thumbnailUrl?: string;
  /** Full uploaded image before crop (optional). */
  thumbnailOriginalUrl?: string;
  /** Cropped image used on profile cards (optional). */
  thumbnailCroppedUrl?: string;
  /** Crop area in pixels; optional, for re-use. */
  thumbnailCrop?: ThumbnailCrop;
  /** Aspect ratio used for crop (e.g. 16/9). */
  thumbnailAspect?: number;
}

/**
 * Portfolio content item
 */
export interface PortfolioItem {
  id: string;
  title: string;
  description?: string;
  thumbnailUrl?: string;
  contentUrl: string; // Link to TikTok/Reel/etc.
  platformName?: string;
  createdAt: number; // Unix timestamp
}

/**
 * Main influencer profile document
 * 
 * Stored in: `influencers/{userId}`
 * 
 * Query patterns:
 * - By userId: Direct document read (fast)
 * - By username: Query with where('username', '==', username) - requires index
 */
export interface InfluencerProfile {
  id: string; // Document ID (same as userId)
  userId: string; // Firebase Auth UID
  username: string; // Unique, lowercase, URL-safe handle
  displayName: string;
  creatorTitle?: string; // Short tagline displayed under the username (e.g., "Lifestyle & travel creator")
  bio: string;
  aboutMe?: string; // Short bio/story for the creator
  niche: string[]; // Multiple niches supported (legacy - use niches instead)
  niches?: string[]; // Array of niche strings (max 3) - preferred field
  profileImageUrl?: string;
  bannerImageUrl?: string;
  platforms: Platform[];
  stats: {
    /** @deprecated totalFollowers is no longer stored manually. It is computed from platformStats only. */
    totalFollowers?: number;
    engagementRate?: number;
  };
  links: Link[];
  collaborations: Collaboration[];
  portfolio: PortfolioItem[];
  featuredVideos?: FeaturedVideo[]; // Featured content highlights
  featuredPosts?: FeaturedPost[]; // Featured posts with thumbnails
  brandPartners?: BrandPartner[]; // Brand partner logos
  location?: string; // Location string
  contactEmail?: string; // Public contact email for brands (media kit)
  identity?: {
    displayName?: string;
    handle?: string;
    creatorTitle?: string; // Short tagline displayed under the username
    subtitle?: string;
    location?: string;
    primaryPlatforms?: string[];
    /** @deprecated totalFollowersOverride is no longer used. Total followers are computed from platformStats only. */
    totalFollowersOverride?: number;
  };
  platformStats?: {
    tiktok?: { followers?: number; avgViews?: number; engagementRate?: number; postsPerWeek?: number; totalViews?: number; totalEngagements?: number };
    instagram?: { followers?: number; avgViews?: number; engagementRate?: number; postsPerWeek?: number; totalViews?: number; totalEngagements?: number };
    youtube?: { followers?: number; avgViews?: number; engagementRate?: number; postsPerWeek?: number; totalViews?: number; totalEngagements?: number };
  };
  /** Manual social list (array). Legacy: may be object map (platform -> url); use getPlatformsFromProfile/socialLinksToArray for reads. */
  socialLinks?: SocialLink[] | Record<string, string>;
  audience?: {
    gender?: { femalePercent?: number; malePercent?: number; otherPercent?: number };
    ageBrackets?: { '13-17'?: number; '18-24'?: number; '25-34'?: number; '35+'?: number };
    topCountries?: Array<{ name: string; percent: number }>;
    topCities?: Array<{ name: string; percent: number }>;
    primaryLanguage?: string;
    topInterests?: string[];
  };
  collabHighlights?: Collaboration[];
  reliability?: {
    campaignsCompleted?: number;
    onTimeRatePercent?: number;
    avgTurnaroundDays?: number;
    repeatBrandRatePercent?: number;
  };
  sectionsEnabled?: {
    platformPerformance?: boolean;
    audience?: boolean;
    brandPartners?: boolean;
    collaborations?: boolean;
    featuredPosts?: boolean;
  };
  /** Consent for public data usage (e.g. Apify TikTok auto-population). Only run refresh when true. */
  socialConsent?: {
    publicData?: boolean;
  };
  /** Auto-populated social account data (e.g. from Apify). Do not show "verified" badges. */
  socialAccounts?: {
    tiktok?: {
      handle?: string;
      profileUrl?: string;
      followersCount?: number;
      lastRefreshedAt?: unknown;
      derived?: {
        avgViews10?: number;
        medianViews10?: number;
        erFollowersMedian?: number;
        viewsPerFollowerMedian10?: number;
        /** IQR/median; preferred for Performance stability */
        stabilitySpreadRatio?: number | null;
        performanceStabilityLabel?: 'stable' | 'variable' | 'spiky' | 'unknown';
        /** @deprecated Prefer performanceStabilityLabel; kept for backward compat */
        cvViews?: number;
        /** @deprecated Prefer performanceStabilityLabel; map consistent→stable, mixed→variable, volatile→spiky */
        consistencyLabel?: string;
      };
      posts?: unknown[];
      raw?: { apifyActorId?: string; fetchedAtISO?: string; itemCount?: number; rawRef?: string };
    };
    instagram?: {
      handle?: string;
      profileUrl?: string;
      followersCount?: number | null;
      lastRefreshedAt?: unknown;
      pendingRefresh?: boolean;
      derived?: {
        avgViews10?: number | null;
        medianViews10?: number | null;
        erFollowersMedian?: number | null;
        viewsPerFollowerMedian10?: number | null;
        stabilitySpreadRatio?: number | null;
        performanceStabilityLabel?: 'stable' | 'variable' | 'spiky' | 'unknown';
        stabilitySeriesUsed?: 'views' | 'engagement' | 'unknown';
        medianEngagement10?: number | null;
        coverage?: { postsConsidered?: number; viewsAvailable?: number; followersKnown?: boolean };
      };
      posts?: unknown[];
      rawRef?: { collection: string; platform: string; docId: string };
    };
  };
  createdAt: number; // Unix timestamp
  updatedAt: number; // Unix timestamp
}

/**
 * Feed post for influencer updates
 * 
 * Stored in: `influencerFeed/{postId}`
 * 
 * Query pattern:
 * - By influencerId: Query with where('influencerId', '==', id) + orderBy('createdAt', 'desc')
 * - Pagination: Use startAfter with last document snapshot
 */
export interface InfluencerFeedPost {
  id: string;
  influencerId: string;
  type: 'milestone' | 'win' | 'content';
  title: string;
  body?: string;
  mediaUrl?: string;
  createdAt: number; // Unix timestamp
  updatedAt?: number; // Unix timestamp
}

/**
 * Input types for creating/updating
 */
export interface InfluencerProfileInput {
  username: string;
  displayName: string;
  bio: string;
  niche: string[];
  profileImageUrl?: string;
  bannerImageUrl?: string;
  platforms?: Platform[];
  stats?: {
    totalFollowers: number;
    engagementRate?: number;
  };
  links?: Link[];
  collaborations?: Collaboration[];
  portfolio?: PortfolioItem[];
  featuredVideos?: FeaturedVideo[];
  featuredPosts?: FeaturedPost[];
  brandPartners?: BrandPartner[];
  location?: string;
}

export interface InfluencerFeedPostInput {
  type: 'milestone' | 'win' | 'content';
  title: string;
  body?: string;
  mediaUrl?: string;
}

/**
 * Pagination options for feed queries
 */
export interface FeedPaginationOptions {
  limit?: number;
  lastDoc?: QueryDocumentSnapshot;
}

// ============================================================================
// FIRESTORE OPERATIONS
// ============================================================================

const INFLUENCERS_COLLECTION = 'influencers';
const INFLUENCER_FEED_COLLECTION = 'influencerFeed';

/**
 * Get influencer profile by Firebase Auth user ID
 * 
 * @param userId - Firebase Auth UID
 * @returns InfluencerProfile or null if not found
 */
export async function getInfluencerByUserId(
  userId: string,
  options?: { fromServer?: boolean }
): Promise<InfluencerProfile | null> {
  try {
    const influencerRef = doc(db, INFLUENCERS_COLLECTION, userId);
    const influencerSnap = options?.fromServer
      ? await getDocFromServer(influencerRef)
      : await getDoc(influencerRef);
    
    if (!influencerSnap.exists()) {
      return null;
    }
    
    const data = influencerSnap.data();
    return {
      id: influencerSnap.id,
      userId: influencerSnap.id,
      ...data,
      // Convert Firestore Timestamps to Unix timestamps
      createdAt: data.createdAt?.toMillis?.() || data.createdAt || Date.now(),
      updatedAt: data.updatedAt?.toMillis?.() || data.updatedAt || Date.now(),
    } as InfluencerProfile;
  } catch (error) {
    console.error('Error fetching influencer by userId:', error);
    throw error;
  }
}

/**
 * Get influencer profile by username (for public profile URLs)
 * 
 * Searches both top-level 'username' field and 'identity.handle' field
 * 
 * @param username - Username handle (case-insensitive)
 * @returns InfluencerProfile or null if not found
 */
export async function getInfluencerByUsername(
  username: string
): Promise<InfluencerProfile | null> {
  try {
    const raw = username != null ? String(username).trim() : '';
    if (!raw || raw === 'u') {
      console.warn('[getInfluencerByUsername] Invalid username (skip query):', { received: username, type: typeof username });
      return null;
    }
    console.log('[getInfluencerByUsername] passed username (debug):', raw);
    // Normalize: remove @ prefix, trim, lowercase
    let handle = raw.replace(/^@/, '');
    const handleLower = handle.toLowerCase();
    
    // First, try to find by usernameLower field if it exists
    try {
      const qLower = query(
        collection(db, INFLUENCERS_COLLECTION),
        where('usernameLower', '==', handleLower),
        limit(1)
      );
      
      const querySnapshotLower = await getDocs(qLower);
      
      if (!querySnapshotLower.empty) {
        const doc = querySnapshotLower.docs[0];
        const data = doc.data();
        console.log('[getInfluencerByUsername] Found by usernameLower field');
        
        return {
          id: doc.id,
          userId: doc.id,
          ...data,
          createdAt: data.createdAt?.toMillis?.() || data.createdAt || Date.now(),
          updatedAt: data.updatedAt?.toMillis?.() || data.updatedAt || Date.now(),
        } as InfluencerProfile;
      }
    } catch (err) {
      // usernameLower field might not exist in all documents, continue to fallback
      console.log('[getInfluencerByUsername] usernameLower query not available, using fallback');
    }
    
    // Fallback: try to find by top-level username field (case-insensitive comparison)
    // Note: Firestore queries are case-sensitive, so we need to fetch and filter
    const allInfluencersQuery = query(
      collection(db, INFLUENCERS_COLLECTION),
      limit(1000)
    );
    
    const allSnapshot = await getDocs(allInfluencersQuery);
    
    // Try exact match first (case-sensitive)
    for (const doc of allSnapshot.docs) {
      const data = doc.data();
      const topLevelUsername = data.username;
      
      if (topLevelUsername && topLevelUsername.toLowerCase().trim() === handleLower) {
        console.log('[getInfluencerByUsername] Found by top-level username (case-insensitive match)');
        
        return {
          id: doc.id,
          userId: doc.id,
          ...data,
          createdAt: data.createdAt?.toMillis?.() || data.createdAt || Date.now(),
          updatedAt: data.updatedAt?.toMillis?.() || data.updatedAt || Date.now(),
        } as InfluencerProfile;
      }
    }
    
    // If still not found, try to find by identity.handle (backward compatibility)
    console.log('[getInfluencerByUsername] Not found by username fields, searching by identity.handle');
    
    for (const doc of allSnapshot.docs) {
      const data = doc.data();
      const identityHandle = data.identity?.handle?.toLowerCase()?.trim();
      
      if (identityHandle === handleLower) {
        console.log('[getInfluencerByUsername] Found by identity.handle');
        
        // If found by identity.handle but username is missing, sync it
        if (!data.username && data.identity?.handle) {
          try {
            const influencerRef = doc(db, INFLUENCERS_COLLECTION, doc.id);
            await updateDoc(influencerRef, {
              username: data.identity.handle.trim().replace(/^@/, ''),
              updatedAt: serverTimestamp(),
            });
            console.log('[getInfluencerByUsername] Synced identity.handle to top-level username');
          } catch (syncError) {
            console.error('[getInfluencerByUsername] Error syncing username:', syncError);
            // Continue anyway - we still return the profile
          }
        }
        
        return {
          id: doc.id,
          userId: doc.id,
          ...data,
          createdAt: data.createdAt?.toMillis?.() || data.createdAt || Date.now(),
          updatedAt: data.updatedAt?.toMillis?.() || data.updatedAt || Date.now(),
        } as InfluencerProfile;
      }
    }
    
    console.log('[getInfluencerByUsername] Profile not found for:', { original: raw, handle, handleLower });
    return null;
  } catch (error) {
    console.error('[getInfluencerByUsername] Error fetching influencer by username:', error);
    throw error;
  }
}

/**
 * Create or update influencer profile
 * 
 * @param userId - Firebase Auth UID (used as document ID)
 * @param profileData - Profile data to save
 */
export async function createOrUpdateInfluencerProfile(
  userId: string,
  profileData: Partial<InfluencerProfileInput>
): Promise<void> {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const influencerRef = doc(db, INFLUENCERS_COLLECTION, userId);
    
    // Normalize username to lowercase for consistent lookups
    const normalizedData: any = {
      ...profileData,
      updatedAt: serverTimestamp(),
    };
    
    // Determine the username to use (from identity.handle or direct username field)
    let usernameToClaim: string | null = null;
    let previousUsername: string | null = null;
    
    // Get current username from existing profile if it exists
    const existingDoc = await getDoc(influencerRef);
    if (existingDoc.exists()) {
      const existingData = existingDoc.data();
      previousUsername = existingData.username || existingData.identity?.handle || null;
    }
    
    // Always sync identity.handle to top-level username for consistent lookups
    // This ensures profiles can be found by username even if only identity.handle is set
    if (profileData.identity?.handle) {
      const normalizedHandle = profileData.identity.handle.toLowerCase().trim();
      // Sync to top-level username if username is not provided or differs
      if (!normalizedData.username || normalizedData.username.toLowerCase().trim() !== normalizedHandle) {
        console.log('[createOrUpdateInfluencerProfile] Syncing identity.handle to username:', {
          identityHandle: profileData.identity.handle,
          normalizedHandle,
          currentUsername: normalizedData.username,
        });
        normalizedData.username = normalizedHandle;
        usernameToClaim = normalizedHandle;
      }
    }
    
    // Also ensure username is set if provided directly
    if (profileData.username && !normalizedData.username) {
      normalizedData.username = profileData.username.toLowerCase().trim();
      usernameToClaim = normalizedData.username;
      console.log('[createOrUpdateInfluencerProfile] Setting username from profileData:', normalizedData.username);
    }
    
    // Normalize username if it's provided
    if (normalizedData.username) {
      normalizedData.username = normalizedData.username.toLowerCase().trim();
      if (!usernameToClaim) {
        usernameToClaim = normalizedData.username;
      }
    }
    
    // Claim username using the username service (atomic transaction)
    // This ensures uniqueness and handles username changes properly
    if (usernameToClaim) {
      try {
        await claimUsernameForUser({
          userId,
          newUsername: usernameToClaim,
          previousUsername: previousUsername || undefined,
        });
        console.log('[createOrUpdateInfluencerProfile] Username claimed successfully:', usernameToClaim);
      } catch (error: any) {
        // If username is already taken, throw a clear error
        if (error.code === 'USERNAME_TAKEN' || error.message === 'USERNAME_TAKEN' || error.message.includes('already taken')) {
          throw new Error(`Username "${usernameToClaim}" is already taken`);
        }
        // Re-throw other errors
        throw error;
      }
    }
    
    // Check if document exists (we already fetched it above for previousUsername)
    if (existingDoc.exists()) {
      // Update existing document - merge nested objects properly
      const existingData = existingDoc.data();
      const updateData: any = {};
      
      Object.keys(normalizedData).forEach(key => {
        if (normalizedData[key] !== undefined) {
          // For nested objects (identity, platformStats, audience, etc.), merge with existing
          if (key === 'identity' && typeof normalizedData[key] === 'object' && !Array.isArray(normalizedData[key])) {
            // Merge identity objects
            const mergedIdentity = {
              ...(existingData[key] || {}),
              ...normalizedData[key],
            };
            // Remove undefined values from identity object (Firestore doesn't allow undefined)
            const cleanIdentity: any = {};
            Object.keys(mergedIdentity).forEach(identityKey => {
              if (mergedIdentity[identityKey] !== undefined) {
                cleanIdentity[identityKey] = mergedIdentity[identityKey];
              }
            });
            // Only include identity if it has at least one key
            if (Object.keys(cleanIdentity).length > 0) {
              updateData[key] = cleanIdentity;
            }
          } else if (key === 'platformStats' && typeof normalizedData[key] === 'object' && !Array.isArray(normalizedData[key])) {
            updateData[key] = {
              ...(existingData[key] || {}),
              ...normalizedData[key],
            };
          } else if (key === 'audience' && typeof normalizedData[key] === 'object' && !Array.isArray(normalizedData[key])) {
            // Merge audience data and remove undefined values
            const mergedAudience = {
              ...(existingData[key] || {}),
              ...normalizedData[key],
            };
            // Remove undefined values from nested objects
            const cleanAudience: any = {};
            Object.keys(mergedAudience).forEach(audKey => {
              const audValue = mergedAudience[audKey];
              if (audValue !== undefined) {
                if (typeof audValue === 'object' && !Array.isArray(audValue)) {
                  // Clean nested objects (gender, ageBrackets)
                  const cleanNested: any = {};
                  Object.keys(audValue).forEach(nestedKey => {
                    if (audValue[nestedKey] !== undefined) {
                      cleanNested[nestedKey] = audValue[nestedKey];
                    }
                  });
                  if (Object.keys(cleanNested).length > 0) {
                    cleanAudience[audKey] = cleanNested;
                  }
                } else if (Array.isArray(audValue)) {
                  // Clean arrays (topCountries, topCities)
                  const cleanArray = audValue
                    .map(item => {
                      if (typeof item === 'object' && item !== null) {
                        const cleanItem: any = {};
                        Object.keys(item).forEach(itemKey => {
                          if (item[itemKey] !== undefined) {
                            cleanItem[itemKey] = item[itemKey];
                          }
                        });
                        return Object.keys(cleanItem).length > 0 ? cleanItem : null;
                      }
                      return item !== undefined ? item : null;
                    })
                    .filter(item => item !== null && item !== undefined);
                  if (cleanArray.length > 0) {
                    cleanAudience[audKey] = cleanArray;
                  }
                } else {
                  cleanAudience[audKey] = audValue;
                }
              }
            });
            if (Object.keys(cleanAudience).length > 0) {
              updateData[key] = cleanAudience;
            }
          } else if (key === 'reliability' && typeof normalizedData[key] === 'object' && !Array.isArray(normalizedData[key])) {
            updateData[key] = {
              ...(existingData[key] || {}),
              ...normalizedData[key],
            };
          } else if (key === 'socialAccounts' && typeof normalizedData[key] === 'object' && !Array.isArray(normalizedData[key])) {
            updateData[key] = {
              ...(existingData[key] || {}),
              ...normalizedData[key],
            };
          } else {
            updateData[key] = normalizedData[key];
          }
        }
      });
      
      console.log('[createOrUpdateInfluencerProfile] ========== SAVING TO FIRESTORE ==========');
      console.log('[createOrUpdateInfluencerProfile] Update data keys:', Object.keys(updateData));
      console.log('[createOrUpdateInfluencerProfile] Full update data:', JSON.parse(JSON.stringify(updateData)));
      console.log('[createOrUpdateInfluencerProfile] Update data check:', {
        hasBio: !!updateData.bio,
        bio: updateData.bio,
        hasPlatformStats: !!updateData.platformStats && Object.keys(updateData.platformStats || {}).length > 0,
        platformStatsKeys: updateData.platformStats ? Object.keys(updateData.platformStats) : [],
        hasContactEmail: !!updateData.contactEmail,
        contactEmail: updateData.contactEmail,
        hasPhotoURL: !!updateData.photoURL,
        photoURL: updateData.photoURL,
        hasProfileImageUrl: !!updateData.profileImageUrl,
        profileImageUrl: updateData.profileImageUrl,
        hasAvatarUrl: !!updateData.avatarUrl,
        avatarUrl: updateData.avatarUrl,
        hasLocation: !!updateData.location,
        location: updateData.location,
        hasNiche: !!updateData.niche,
        niche: updateData.niche,
        hasCreatorTitle: !!updateData.creatorTitle,
        creatorTitle: updateData.creatorTitle,
      });
      console.log('[createOrUpdateInfluencerProfile] =========================================');
      
      const sanitizedUpdateData = sanitizeForFirestore(updateData);
      await updateDoc(influencerRef, sanitizedUpdateData);
      
      // Verify the data was saved by reading it back
      const verifyDoc = await getDoc(influencerRef);
      if (verifyDoc.exists()) {
        const savedData = verifyDoc.data();
        console.log('[createOrUpdateInfluencerProfile] ========== VERIFICATION READ ==========');
        console.log('[createOrUpdateInfluencerProfile] Saved data keys:', Object.keys(savedData));
        console.log('[createOrUpdateInfluencerProfile] Verification check:', {
          hasBio: !!savedData.bio,
          bio: savedData.bio,
          hasPlatformStats: !!savedData.platformStats && Object.keys(savedData.platformStats || {}).length > 0,
          platformStatsKeys: savedData.platformStats ? Object.keys(savedData.platformStats) : [],
          hasContactEmail: !!savedData.contactEmail,
          contactEmail: savedData.contactEmail,
          hasPhotoURL: !!savedData.photoURL,
          photoURL: savedData.photoURL,
          hasProfileImageUrl: !!savedData.profileImageUrl,
          profileImageUrl: savedData.profileImageUrl,
          hasAvatarUrl: !!savedData.avatarUrl,
          avatarUrl: savedData.avatarUrl,
        });
        console.log('[createOrUpdateInfluencerProfile] ======================================');
      }
      
      console.log('[createOrUpdateInfluencerProfile] Profile updated successfully');
    } else {
      // Create new document
      const newData: any = {
        ...normalizedData,
        userId,
        createdAt: serverTimestamp(),
      };
      // Remove undefined values
      Object.keys(newData).forEach(key => {
        if (newData[key] === undefined) {
          delete newData[key];
        }
      });
      
      console.log('[createOrUpdateInfluencerProfile] Creating new profile with data:', {
        userId,
        username: newData.username,
        identityHandle: newData.identity?.handle,
        displayName: newData.displayName,
      });
      
      const sanitizedNewData = sanitizeForFirestore(newData);
      await setDoc(influencerRef, sanitizedNewData);
      console.log('[createOrUpdateInfluencerProfile] Profile created successfully');
    }
  } catch (error: any) {
    console.error('Error upserting influencer profile:', error);
    // Provide more helpful error messages
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied. Please check your Firestore security rules.');
    } else if (error.code === 'unavailable') {
      throw new Error('Firestore is temporarily unavailable. Please try again.');
    } else if (error.message) {
      throw error; // Re-throw with original message
    } else {
      throw new Error(`Failed to save profile: ${error.toString()}`);
    }
  }
}

/**
 * Check if username is available
 * 
 * @param username - Username to check
 * @returns true if available, false if taken
 */
export async function isUsernameAvailable(username: string): Promise<boolean> {
  try {
    const influencer = await getInfluencerByUsername(username);
    return influencer === null;
  } catch (error) {
    console.error('Error checking username availability:', error);
    return false;
  }
}

// ============================================================================
// FEED OPERATIONS
// ============================================================================

/**
 * List feed posts for an influencer with pagination
 * 
 * Requires Firestore index: influencerFeed(influencerId ASC, createdAt DESC)
 * 
 * @param influencerId - Influencer user ID
 * @param options - Pagination options
 * @returns Array of feed posts and last document for pagination
 */
export async function listInfluencerFeedPosts(
  influencerId: string,
  options: FeedPaginationOptions = {}
): Promise<{
  posts: InfluencerFeedPost[];
  lastDoc: QueryDocumentSnapshot | null;
}> {
  try {
    const limitCount = options.limit || 10;
    let q = query(
      collection(db, INFLUENCER_FEED_COLLECTION),
      where('influencerId', '==', influencerId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    // Add pagination cursor if provided
    if (options.lastDoc) {
      q = query(q, startAfter(options.lastDoc));
    }
    
    const querySnapshot = await getDocs(q);
    const posts = querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toMillis?.() || data.createdAt || Date.now(),
        updatedAt: data.updatedAt?.toMillis?.() || data.updatedAt,
      } as InfluencerFeedPost;
    });
    
    const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
    
    return { posts, lastDoc };
  } catch (error) {
    console.error('Error fetching feed posts:', error);
    throw error;
  }
}

/**
 * Add a new feed post
 * 
 * @param influencerId - Influencer user ID
 * @param postData - Post data
 * @returns Created post ID
 */
export async function addInfluencerFeedPost(
  influencerId: string,
  postData: InfluencerFeedPostInput
): Promise<string> {
  try {
    const feedRef = collection(db, INFLUENCER_FEED_COLLECTION);
    const newPostDoc = doc(feedRef);
    
    const post = {
      influencerId,
      ...postData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    
    await setDoc(newPostDoc, post);
    return newPostDoc.id;
  } catch (error) {
    console.error('Error adding feed post:', error);
    throw error;
  }
}

/**
 * Update a feed post
 * 
 * @param postId - Post document ID
 * @param updates - Partial post data to update
 */
export async function updateInfluencerFeedPost(
  postId: string,
  updates: Partial<InfluencerFeedPostInput>
): Promise<void> {
  try {
    const postRef = doc(db, INFLUENCER_FEED_COLLECTION, postId);
    await updateDoc(postRef, {
      ...updates,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating feed post:', error);
    throw error;
  }
}

/**
 * Delete a feed post (soft delete)
 * 
 * @param postId - Post document ID
 */
export async function deleteInfluencerFeedPost(postId: string): Promise<void> {
  try {
    const postRef = doc(db, INFLUENCER_FEED_COLLECTION, postId);
    await updateDoc(postRef, {
      deleted: true,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error deleting feed post:', error);
    throw error;
  }
}

// ============================================================================
// FUTURE EXTENSIBILITY NOTES
// ============================================================================

/**
 * FUTURE: Brand Search & Filtering
 * 
 * To enable brand-facing search of influencers, add:
 * 
 * 1. New function: searchInfluencers(filters: InfluencerSearchFilters)
 *    - Filters: niche, minFollowers, maxFollowers, platforms, etc.
 *    - Location: /lib/models/influencer.ts (or new /lib/models/search.ts)
 * 
 * 2. Firestore indexes:
 *    - influencers(niche ASC, stats.totalFollowers DESC)
 *    - influencers(platforms.platformName ASC, platforms.followers DESC)
 * 
 * 3. Brand search UI: /app/brand/search (future)
 * 
 * 4. Keep influencer data clean - do NOT add brand-specific fields to
 *    InfluencerProfile. Use separate collections:
 *    - brands/{brandId}
 *    - campaigns/{campaignId} (references influencerId)
 *    - applications/{applicationId} (references influencerId and campaignId)
 */

